import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamService } from '../services/api';
import { Table, Pagination, StatusToggle, PageHeader, useToast, Card } from './common';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import usePageState from '../hooks/usePageState';
import { useAuth } from '../context/AuthContext';

const TeamManager = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getPagePermissions } = useAuth();
  const permissions = getPagePermissions('/admin/teams');
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30
  });

  const [filters, setFilters] = usePageState('teamManager', {
    currentPage: 1,
    itemsPerPage: 30,
    search: '',
    startDate: '',
    endDate: '',
  }, ['/admin/teams']);
  const { currentPage, itemsPerPage, search, startDate, endDate } = filters;
  const setCurrentPage  = (v) => setFilters({ currentPage: v });
  const setItemsPerPage = (v) => setFilters({ itemsPerPage: v });
  const setSearch       = (v) => setFilters({ search: v });
  const setStartDate    = (v) => setFilters({ startDate: v });
  const setEndDate      = (v) => setFilters({ endDate: v });

  useEffect(() => {
    setPageTitle('Teams');
  }, [setPageTitle]);

  useEffect(() => {
    fetchTeams();
  }, [currentPage, search, startDate, endDate, itemsPerPage]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      
      if (search) params.search = search;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await teamService.getAll(params);
      setTeams(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to fetch teams');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleDateRangeFilter = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleView = (team) => {
    navigate(`/admin/teams/view/${team.id}`);
  };

  const handleEdit = (team) => {
    navigate(`/admin/teams/edit/${team.id}`);
  };

  const handleAddTeam = () => {
    navigate('/admin/teams/create');
  };

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this team?');
    if (confirmed) {
      try {
        await teamService.delete(id);
        toast.error('Team deleted successfully');
        fetchTeams();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete team');
        console.error(err);
      }
    }
  };

  const handleStatusToggle = async (team, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [team.id]: true }));
    
    try {
      await teamService.updateStatus(team.id, newStatus);
      toast.success(`Team ${newStatus ? 'activated' : 'deactivated'} successfully`);
      
      // Refresh the teams list
      await fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update team status');
      console.error(err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [team.id]: false }));
    }
  };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader 
          filters={{
            search: {
              placeholder: 'Search by Team Name, User, Region, or Category...',
              value: search,
              onChange: (e) => handleSearch(e.target.value)
            },
            // dateRange: {
            //   startDate: startDate,
            //   endDate: endDate,
            //   onStartDateChange: (e) => handleDateRangeFilter(e.target.value, endDate),
            //   onEndDateChange: (e) => handleDateRangeFilter(startDate, e.target.value)
            // }
          }}
          addButton={permissions.can_add ? {
            label: 'Add Team',
            onClick: handleAddTeam,
            variant: 'primary'
          } : null}
        />
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <Table
              columns={[
                { 
                  header: 'S.No', 
                  accessor: 'serial',
                  width: '80px',
                  render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
                },
                { 
                  header: 'Name', 
                  accessor: 'name',
                  width: '180px',
                  render: (value) => (
                    <span className="text-sm text-gray-900 font-medium" title={value}>
                      {value && value.length > 25 ? `${value.substring(0, 25)}...` : value}
                    </span>
                  )
                },
                { 
                  header: 'Regions', 
                  accessor: 'region_names',
                  width: '200px',
                  render: (value) => value ? (
                    <span className="text-sm text-gray-700" title={value}>
                      {value.length > 35 ? `${value.substring(0, 35)}...` : value}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">No regions</span>
                  )
                },
                { 
                  header: 'Categories', 
                  accessor: 'category_names',
                  width: '200px',
                  render: (value) => value ? (
                    <span className="text-sm text-gray-700" title={value}>
                      {value.length > 35 ? `${value.substring(0, 35)}...` : value}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">No categories</span>
                  )
                },
                { 
                  header: 'Users', 
                  accessor: 'user_names',
                  width: '200px',
                  render: (value) => value ? (
                    <span className="text-sm text-gray-700" title={value}>
                      {value.length > 35 ? `${value.substring(0, 35)}...` : value}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">No users</span>
                  )
                },
                {
                  header: 'Created',
                  accessor: 'created_at',
                  width: '110px',
                  render: (value) => formatDateDDMMYYYY(value),
                },
                {
                  header: 'Status',
                  accessor: 'status',
                  width: '100px',
                  align: 'center',
                  render: (value, row) => (
                    <StatusToggle
                      status={value}
                      onChange={(newStatus) => handleStatusToggle(row, newStatus)}
                      loading={updatingStatus[row.id]}
                      disabled={!permissions.can_edit}
                    />
                  )
                },
                {
                  header: 'Actions',
                  accessor: 'actions',
                  width: '100px',
                  align: 'center',
                  actions: [
                    ...(permissions.can_edit ? [{ type: 'edit', onClick: handleEdit }] : []),
                    ...(permissions.can_delete ? [{ type: 'delete', onClick: (row) => handleDelete(row.id) }] : [])
                  ]
                },
              ]}
              data={permissions.can_list ? teams : []}
              onRowClick={permissions.can_view ? handleView : undefined}
              emptyMessage={!permissions.can_list ? 'Permission Denied - You don\'t have permission to view this data' : 'No teams found'}
              loading={loading}
            />
            
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
            
      </Card>
    </div>
  );
};

export default TeamManager;
